import { Type } from '@angular/core';
import { createSelector, Selector } from '@ngxs/store';
import _ from 'lodash';
import { BrickNotAllowedBrickComponent } from 'src/app/components/bricks/error/brick-not-allowed-brick/brick-not-allowed-brick.component';
import { BrickNotFoundBrickComponent } from 'src/app/components/bricks/error/brick-not-found-brick/brick-not-found-brick.component';
import { BrickNotReadyBrickComponent } from 'src/app/components/bricks/error/brick-not-ready-brick/brick-not-ready-brick.component';
import { ISpecificBrickSettingsComponent } from 'src/app/components/dashboard/brick-settings/brick-specific-type-settings/ISpecificBrickSettingsComponent';
import { mapBrickInstanceToGridster } from 'src/app/components/dashboard/brick-wall/helper/GridsterSamwinBrickConverter';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { BrickDefinition } from 'src/app/samwebDescription/samweb/brickDefinition';
import { BrickLayoutType } from 'src/app/samwebDescription/samweb/dashboard/brickLayoutType';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { BrickDefinitionState } from 'src/app/store/dashboard/brick-wall/brick-definition/brick-definition.state';
import { BrickInstance } from 'src/app/store/dashboard/brick-wall/brick-wall.state';
import { PilotPanelState } from 'src/app/store/dashboard/pilot-panel/pilot-panel.state';
import { Sccs1ConnectionState } from 'src/app/store/server-communication/sccs1-connection/sccs1-connection.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { BrickInstancesState, BrickInstancesStateModel, IBrickDescription } from './brick-instances.state';

export class BrickInstancesSelectors {
   public static numberOfBricksOnWallForBrickDefinition(brickDefinitionId: string) {
      return createSelector([BrickInstancesSelectors.brickWallCurrentInstances], (brickInstances) => {
         return _(brickInstances)
            .filter((x) => x.brickDefinitionId === brickDefinitionId)
            .value().length;
      });
   }

   @Selector([BrickInstancesState])
   public static isSavingBrickWall(state: BrickInstancesStateModel) {
      return !state.isSavingBrickWall;
   }

   @Selector([BrickInstancesState])
   public static isBrickWallEditable(state: BrickInstancesStateModel): boolean {
      return state.isWallEditingEnabled;
   }

   @Selector([BrickInstancesState])
   public static brickWallInstances(state: BrickInstancesStateModel): { [p: string]: BrickInstance } {
      return state.brickInstances;
   }

   @Selector([BrickInstancesState])
   public static brickWallEditInstances(state: BrickInstancesStateModel): { [p: string]: BrickInstance } {
      return state.brickInstancesEditMode;
   }

   @Selector([BrickInstancesSelectors.isBrickWallEditable, BrickInstancesSelectors.brickWallInstances, BrickInstancesSelectors.brickWallEditInstances])
   public static brickWallCurrentInstances(
      isEnabled: boolean,
      instance: { [p: string]: BrickInstance },
      editInstance: { [p: string]: BrickInstance }
   ): { [p: string]: BrickInstance } {
      return isEnabled ? editInstance : instance;
   }

   @Selector([BrickInstancesState])
   public static persistedBrickWallInstanceIds(state: BrickInstancesStateModel): string[] {
      return _.keys(state.brickInstances);
   }

   @Selector([BrickInstancesSelectors.brickWallCurrentInstances])
   public static allBrickInstances(brickInstances: { [p: string]: BrickInstance }): any {
      return _(brickInstances).keys().value();
   }

   public static brickInstanceIdsForBrickWall(pilotPanel: boolean) {
      return createSelector(
         [BrickInstancesSelectors.brickWallCurrentInstances, PilotPanelState.isPilotPanelExtended, BrickDefinitionState.brickDefinitionByIdFn],
         (brickInstances: BrickInstance[], isPilotPanelExtended: boolean, brickDefinitionByIdFn: (brickDefinitionId: string) => BrickDefinition) => {
            return _(brickInstances)
               .pickBy((x) => {
                  const brickDefinition = brickDefinitionByIdFn(x.brickDefinitionId);
                  if (pilotPanel && !isPilotPanelExtended && _.isNil(brickDefinition?.smallBrickComponentAlternative)) {
                     return false;
                  }
                  if (_.isNil(x.positionX) || _.isNil(x.positionY)) {
                     return false;
                  }

                  return pilotPanel ? x.brickLayout.value === BrickLayoutType.PILOT_PANEL.value : x.brickLayout.value === BrickLayoutType.DEFAULT_WALL.value;
               })
               .keys()
               .value();
         }
      );
   }

   @Selector([EnabledFeaturesState.getEnabledFeatures])
   public static isBrickUsageAllowedFn(features: FeatureEnum[]) {
      return (brickDefinition: BrickDefinition) => {
         if (brickDefinition.brickCategory === BrickCategory.DEBUG && localStorage.getItem('ENABLE_DEBUG_BRICKS') !== 'true') {
            return false;
         }

         if (_.isNil(brickDefinition.requiredFeature)) {
            return true;
         }

         return _(features).includes(brickDefinition.requiredFeature);
      };
   }

   @Selector([Sccs1ConnectionState.anyConnectionInUse])
   public static isReadyToUseFn(anyConnectionInUse: boolean) {
      return (brickDefinition: BrickDefinition) => {
         // not specified, brick is ready to use
         if (_.isNil(brickDefinition.requiresActiveCoreConnection)) {
            return true;
         }

         // set to false -> no core service connection required
         if (!brickDefinition.requiresActiveCoreConnection) {
            return true;
         }

         // readiness depends on core service connection
         return anyConnectionInUse;
      };
   }

   @Selector([
      BrickInstancesSelectors.brickWallCurrentInstances,
      BrickDefinitionState.brickDefinitionsAsArray,
      BrickInstancesSelectors.isBrickUsageAllowedFn,
      BrickInstancesSelectors.isReadyToUseFn,
   ])
   public static brickDescriptionFn(brickInstances, brickDefinitions, isBrickUsageAllowedFn, isReadyToUseFn): (brickInstanceId: string) => IBrickDescription {
      return (brickInstanceId: string) => {
         let brickInstance = brickInstances[brickInstanceId];

         if (_.isNil(brickInstance)) {
            brickInstance = {
               brickLayout: BrickLayoutType.DEFAULT_WALL,
               brickDefinitionId: BrickNotFoundBrickComponent.BrickId,
               brickInstanceId: brickInstanceId,
            };
         }
         let brickDefinition: BrickDefinition = brickDefinitions[brickInstance.brickDefinitionId];
         if (_.isNil(brickDefinition)) {
            brickDefinition = brickDefinitions[BrickNotFoundBrickComponent.BrickId];
         } else if (!isReadyToUseFn(brickDefinition)) {
            brickDefinition = brickDefinitions[BrickNotReadyBrickComponent.BrickId];
         } else if (!isBrickUsageAllowedFn(brickDefinition)) {
            brickDefinition = brickDefinitions[BrickNotAllowedBrickComponent.BrickId];
         }

         return <IBrickDescription>{
            brickInstanceId: brickInstanceId,
            brickInstance: brickInstance,
            definition: brickDefinition,
         };
      };
   }

   @Selector([BrickInstancesSelectors.brickWallCurrentInstances, BrickDefinitionState.brickDefinitionsAsArray])
   public static brickSettingDescriptionFn(brickInstances, brickDefinitions): (brickInstanceId: string) => IBrickDescription {
      return (brickInstanceId: string) => {
         let brickInstance = brickInstances[brickInstanceId];

         if (_.isNil(brickInstance)) {
            brickInstance = {
               brickLayout: BrickLayoutType.DEFAULT_WALL,
               brickDefinitionId: BrickNotFoundBrickComponent.BrickId,
               brickInstanceId: brickInstanceId,
            };
         }
         let brickDefinition: BrickDefinition = brickDefinitions[brickInstance.brickDefinitionId];
         if (_.isNil(brickDefinition)) {
            brickDefinition = brickDefinitions[BrickNotFoundBrickComponent.BrickId];
         }

         return <IBrickDescription>{
            brickInstanceId: brickInstanceId,
            brickInstance: brickInstance,
            definition: brickDefinition,
         };
      };
   }

   @Selector([BrickInstancesSelectors.brickDescriptionFn, BrickInstancesSelectors.isBrickWallEditable, PilotPanelState.isPilotPanelExtended])
   public static gridsterItemForBrickInstanceFn(
      brickDescriptionFn: (brickInstanceId: string) => IBrickDescription,
      isEditable: boolean,
      isPilotPanelExtended: boolean
   ) {
      return (brickInstanceId: string, brickEditPossible = true) => {
         const brickDescription = brickDescriptionFn(brickInstanceId);
         if (_.isNil(brickDescription)) {
            return null;
         }
         return mapBrickInstanceToGridster(isEditable && brickEditPossible, isPilotPanelExtended, brickDescription);
      };
   }

   @Selector([BrickInstancesSelectors.brickDescriptionFn])
   public static getBrickSettingsComponentFn(
      brickDescriptionFn: (brickInstanceId: string) => IBrickDescription
   ): (brickInstanceId: string) => Type<ISpecificBrickSettingsComponent> {
      return (brickInstanceId: string) => {
         const brickDescription = brickDescriptionFn(brickInstanceId);
         if (_.isNil(brickDescription) || _.isNil(brickDescription.definition)) {
            return null;
         }
         return brickDescription.definition.settingsComponent;
      };
   }
}
